import type { SVGProps } from 'react';
import { memo, useEffect, useRef, useState } from 'react';

import { Typography } from '@capsule/consumer-design-system';

import { useMediaQuery } from '@react-hookz/web';
import { useIdleTimer } from 'react-idle-timer';
import styled, { css, useTheme } from 'styled-components';

import type { AddonOTCItemProps } from 'src/app/components/Pages/Essentials/CheckoutAddons/AddonOTCItem';
import { cartAddItems, cartUpdateItem, cartRemoveItems } from 'src/app/state/actions';
import { getCartItems } from 'src/app/state/selectors';
import { useDispatch, useSelector } from 'src/app/state/store';
import { ONE_SECOND } from 'src/consts/unitsOfTime';

const Svg = styled.svg`
  width: ${({ theme }) => theme.space.s6};
  height: ${({ theme }) => theme.space.s6};
`;

const ExpandedPlusButton = ({ onClick }: Pick<SVGProps<SVGSVGElement>, 'onClick'>) => (
  <Svg
    onClick={onClick}
    viewBox="0 0 32 32"
    css={css`
      border-radius: ${({ theme }) => theme.radii.circle};

      ${({ theme }) => theme.mediaQueries.hasHover} {
        &:hover circle {
          fill: ${({ theme }) => theme.colors.capsuleGreen80};
        }
      }

      &:active circle {
        fill: ${({ theme }) => theme.colors.capsuleGreen80};
      }
    `}
  >
    <circle cx="16" cy="16" r="16" stroke="none" fill="none" />

    {/* Plus Icon */}
    <g>
      <path
        fill="currentColor"
        stroke="currentColor"
        d="M10.5 16c0-.28.22-.5.5-.5h10a.5.5 0 0 1 0 1H11a.5.5 0 0 1-.5-.5Z"
      />
      <path
        fill="currentColor"
        stroke="currentColor"
        d="M16 10.5c.28 0 .5.22.5.5v10a.5.5 0 0 1-1 0V11c0-.28.22-.5.5-.5Z"
      />
    </g>
  </Svg>
);

const ExpandedMinusButton = ({ onClick }: Pick<SVGProps<SVGSVGElement>, 'onClick'>) => (
  <Svg
    onClick={onClick}
    viewBox="0 0 32 32"
    css={css`
      border-radius: ${({ theme }) => theme.radii.circle};

      ${({ theme }) => theme.mediaQueries.hasHover} {
        &:hover circle {
          fill: ${({ theme }) => theme.colors.capsuleGreen80};
        }
      }
    `}
  >
    <circle cx="16" cy="16" r="16" stroke="none" fill="none" />
    <path
      fill="currentColor"
      stroke="currentColor"
      d="M10.5 16c0-.28.22-.5.5-.5h10a.5.5 0 0 1 0 1H11a.5.5 0 0 1-.5-.5Z"
    />
  </Svg>
);

const PlusButton = ({
  onClick,
  isPickerVisible,
}: Pick<SVGProps<SVGSVGElement>, 'onClick'> & { isPickerVisible: boolean }) => (
  <Svg
    onClick={onClick}
    viewBox="0 0 32 32"
    css={css`
      position: absolute;
      cursor: ${isPickerVisible ? 'default' : 'pointer'};
      border-radius: ${({ theme }) => theme.radii.circle};
      transition: all ${({ theme }) => theme.animation.typical};
      color: ${({ theme }) => theme.colors.capsuleGreen60};

      & > circle {
        fill: ${({ theme }) => theme.colors.transparent};
        stroke: ${({ theme }) => theme.colors.capsuleGreen60};
        transition: fill ${({ theme }) => theme.animation.typical};
      }

      ${({ theme }) => theme.mediaQueries.hasHover} {
        &:hover {
          color: ${({ theme }) => theme.colors.white};
          transition: all ${({ theme }) => theme.animation.typical};
        }

        &:hover > circle {
          fill: ${({ theme }) => theme.colors.capsuleGreen60};
          transition: fill ${({ theme }) => theme.animation.typical};
        }
      }
    `}
  >
    <circle strokeWidth="2" cx="16" cy="16" r="13" />
    <path stroke="currentColor" d="M10.5 16c0-.28.22-.5.5-.5h10a.5.5 0 0 1 0 1H11a.5.5 0 0 1-.5-.5Z" />
    <path stroke="currentColor" d="M16 10.5c.28 0 .5.22.5.5v10a.5.5 0 0 1-1 0V11c0-.28.22-.5.5-.5Z" />
  </Svg>
);

interface ItemQuantityPickerProps
  extends Pick<AddonOTCItemProps, 'onAddToCart' | 'onRemoveFromCart' | 'price' | 'maxQuantity' | 'quantityInStock'> {
  itemId: string;
}

export const ItemQuantityPicker = memo(
  ({ itemId: otcItemId, maxQuantity = 99, onAddToCart, onRemoveFromCart, price }: ItemQuantityPickerProps) => {
    const theme = useTheme();
    const hasMouse = useMediaQuery(theme.mediaQueries.hasHover.split('@media ').at(-1));
    const dispatch = useDispatch();
    // We used to calculate maximum allowable quantity based on `quantityInStock`, but this is now being handled
    // by the backend. We can now just use `maxQuantity` as the maximum allowable quantity.
    const maxAllowable = maxQuantity;

    /**
     * @see https://react-redux.js.org/api/hooks#stale-props-and-zombie-children
     *
     * If we prop drill `quantityInCart`, the UI will not update based on changed props values.
     * Log statements and the Redux store will reflect changes, but this component itself will not
     * re-render. This is why we use a selector here 🤷🏽‍♂️
     */
    const relevantItem = useSelector(getCartItems)?.find(item => item.otcItemId === otcItemId);
    const quantityInCart = relevantItem?.quantity ?? 0;

    const [isHovering, setIsHovering] = useState(false);
    const markExpandedInterfaceAsHovered = () => {
      if (!hasMouse) return;
      setIsHovering(true);
    };
    const markExpandedInterfaceAsUnhovered = () => {
      if (!hasMouse) return;
      setIsHovering(false);
    };

    const [isPickerVisible, setIsPickerVisible] = useState(false);
    const makePickerVisible = () => setIsPickerVisible(true);
    const makePickerInvisible = () => setIsPickerVisible(false);

    const incrementQuantity = () => {
      const isOutOfStock = quantityInCart >= maxAllowable;

      if (isOutOfStock) return;

      if (quantityInCart === 0) {
        dispatch(cartAddItems({ otcItemId, price, quantity: 1 }));
      } else {
        dispatch(cartUpdateItem({ otcItemId, quantity: quantityInCart + 1 }));
      }

      onAddToCart?.();
    };

    const decrementQuantity = () => {
      if (quantityInCart === 1) {
        dispatch(cartRemoveItems({ otcItemId }));
        makePickerInvisible();
      } else if (quantityInCart === 0) {
        makePickerInvisible();
      } else {
        dispatch(cartUpdateItem({ otcItemId, quantity: quantityInCart - 1 }));
      }

      onRemoveFromCart?.();
    };

    const fieldQuantitySet = (quantity: number) => {
      const isOutOfStock = quantity > maxAllowable;

      // Do nothing
      if (isOutOfStock) return;

      if (quantity < quantityInCart) {
        onRemoveFromCart?.();
      } else {
        onAddToCart?.();
      }

      // TODO: Analytics
      if (quantity === 0) {
        return dispatch(cartRemoveItems({ otcItemId }));
      }

      if (quantityInCart === 0) {
        return dispatch(cartAddItems({ otcItemId, price, quantity: 1 }));
      }

      return dispatch(cartUpdateItem({ otcItemId, quantity }));
    };

    const [idleElement, setIdleElement] = useState<Document | HTMLDivElement>(document);
    const ref = useRef<HTMLDivElement>(null);
    const { start: startIdleTimer, pause: pauseIdleTimer } = useIdleTimer({
      element: idleElement,
      onIdle: makePickerInvisible,
      timeout: ONE_SECOND * 2,
      disabled: isHovering,
    });

    // Don't start idle timer until the correct element is mounted.
    useEffect(() => {
      if (ref.current) {
        setIdleElement(ref.current);
        if (!isHovering) startIdleTimer();
      }
    }, [ref, startIdleTimer, isHovering]);

    return (
      <div
        ref={ref}
        css={css`
          position: relative;
          display: flex;
          left: -1.5rem;

          &:focus-within > svg {
            cursor: default;
          }

          /* Remove the highlight when tapping these buttons on iOS because it looks bad */
          -webkit-tap-highlight-color: transparent;
          & * {
            -webkit-tap-highlight-color: transparent;
          }
        `}
      >
        {/* Expanded UI */}
        <div
          onMouseEnter={markExpandedInterfaceAsHovered}
          onMouseLeave={markExpandedInterfaceAsUnhovered}
          css={css`
            /**
               * This is necessary so that the plus icon of the expanded interface ends up atop the
               * original UI (circle). In other words, this right-aligns the expanded interface.
               */
            position: absolute;
            height: ${({ theme }) => theme.space.s6};
            right: -${({ theme }) => theme.space.s6};

            display: flex;
            flex-direction: row-reverse;
            align-items: center;
            justify-content: center;
            border-radius: ${({ theme }) => theme.radii.r300};
            background-color: ${({ theme }) => theme.colors.capsuleGreen60};
            color: ${({ theme }) => theme.colors.white};

            /* Make the expanded UI visible AND animate upon visibility */
            transition: opacity ${({ theme }) => theme.animation.typical},
              transform ${({ theme }) => theme.animation.typical};

            /**
               * Why 0.88?
               * The + icon button circle has an r value of 13 which means it falls short of the
               * SVG's borders. Transforming in this element has a rightward transform x-axis origin
               * and a default transform y-axis origin. By scaling, to 0.88 the picker matches the
               * dimensions well enough such that all 3 UI states are flush. A more sustainable way
               * to accomplish the desired UI might have been to use an SVG for both of the
               * non-expanded state UIs and absolutely position the number within the SVG representing
               * the quantity > 0 state.
               */
            transform: scaleX(0.88) scaleY(0.88);
            transform: ${isPickerVisible ? `scaleX(0.88) scaleY(0.88)` : `scaleX(0.2) scaleY(0.88)`};
            transform-origin: right;
            opacity: ${isPickerVisible ? 1 : 0};
            &:focus-within {
              opacity: 1;
              transform: scaleX(0.88) scaleY(0.88);
              transform-origin: right;
              transition: opacity ${({ theme }) => theme.animation.typical},
                transform ${({ theme }) => theme.animation.typical};
            }

            & > svg {
              cursor: ${isPickerVisible ? 'pointer' : 'default'};
            }
          `}
        >
          <ExpandedPlusButton onClick={incrementQuantity} />

          <Typography
            as="input"
            type="number"
            variant="heading4"
            min={0}
            max={100}
            step={undefined}
            required
            value={quantityInCart}
            onFocus={() => {
              // For keyboard users, the expanded UI should stay open when text input is focused
              makePickerVisible();
              pauseIdleTimer();
            }}
            onBlur={() => {
              // When they leave the text input focus, the expanded UI should close after a delay
              startIdleTimer();
            }}
            onChange={event => {
              const { value, valueAsNumber } = event.target;
              return value.trim() === '' ? fieldQuantitySet(1) : fieldQuantitySet(valueAsNumber);
            }}
            css={css`
              /*** ******************************************* ***/
              /*** HIDE NATIVE UP/DOWN BUTTONS IN NUMBER INPUT ***/
              /* Chrome, Safari, Edge, Opera */
              &::-webkit-outer-spin-button,
              &::-webkit-inner-spin-button {
                appearance: none;
                margin: 0;
              }

              /* Firefox */
              appearance: textfield;
              /*** ******************************************* ***/

              background-color: ${({ theme }) => theme.colors.transparent};
              border: none;
              outline: none;

              margin: 0.1rem 0.1rem;
              padding: 0.1rem 0.2rem;
              border-radius: ${({ theme }) => theme.radii.r100};
              &:focus-visible {
                outline: 1px solid ${({ theme }) => theme.colors.white};
              }

              /* We don't want mobile users being able to tap into the input */
              pointer-events: none;
              ${({ theme }) => theme.mediaQueries.hasHover} {
                pointer-events: unset;
              }

              /**
                 * Pixel-perfect nudges 🤷🏽‍♂️
                 * Without these the number doesn't look centered between the buttons.
                 */
              margin-bottom: 1px;
              margin-left: 1px;

              /* Doesn't actually work 😭 */
              font-variant-numeric: tabular-nums;

              /**
                 * TODO: Remove this after fixing web fonts and let font-variant-numeric take over.
                 * Also, add gap s2 to group div if this is removed.
                 */
              width: 2.4rem;
              text-align: center;
            `}
          />

          <ExpandedMinusButton onClick={decrementQuantity} />
        </div>

        {/* Non-expanded UI */}
        <div
          css={css`
            position: absolute;
            opacity: ${isPickerVisible ? 0 : 1};
            visibility: ${isPickerVisible ? 'hidden' : 'visible'};
            transition: opacity ${({ theme }) => theme.animation.typical},
              visibility ${({ theme }) => theme.animation.timing.typical};
          `}
          aria-hidden="true"
        >
          {quantityInCart === 0 ? (
            <PlusButton
              isPickerVisible={isPickerVisible}
              onClick={() => {
                makePickerVisible();
                incrementQuantity();
              }}
            />
          ) : (
            <Typography
              variant="heading4"
              onTouchStart={makePickerVisible}
              onMouseEnter={() => {
                makePickerVisible();
                markExpandedInterfaceAsHovered();

                /**
                 * The + button in the expanded UI wont have the hover styles applied until mouse
                 * move. If we decide to fix this, we'll need to pass an `onMouseLeave` callback to
                 * the button, use local state to track "forcibly hovered", and set that state to
                 * `true` here. Then, we can conditionally apply the hover styles to the + button
                 * based on that state. The `onMouseLeave` callback is necessary because the user
                 * may have hovered the expanded UI, then hovered the + button, then moved the
                 * mouse away from the + button. In this case, we want to remove the forcibly- and
                 * conditionally-applied hover styles.
                 */
              }}
              css={css`
                position: absolute;
                top: 0.2rem;
                left: 0.2rem;
                cursor: ${isPickerVisible ? 'default' : 'pointer'};
                width: 2.8rem;
                height: 2.8rem;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: ${({ theme }) => theme.radii.circle};
                transition: all ${({ theme }) => theme.animation.typical};
                background-color: ${({ theme }) => theme.colors.capsuleGreen60};
                color: ${({ theme }) => theme.colors.white};

                ${({ theme }) => theme.mediaQueries.hasHover} {
                  &:hover {
                    background-color: ${({ theme }) => theme.colors.capsuleGreen80};
                    transition: all ${({ theme }) => theme.animation.typical};
                  }
                }
              `}
            >
              <span
                css={css`
                  /* Helps the animation look a bit crisper since there's never two numbers visible */
                  display: ${isPickerVisible ? 'none' : 'unset'};

                  /* Pixel-perfect nudges 🤷🏽‍♂️ Without this, the number doesn't look vert centered */
                  margin-bottom: 1px;
                `}
              >
                {quantityInCart}
              </span>
            </Typography>
          )}
        </div>
      </div>
    );
  }
);

ItemQuantityPicker.displayName = 'ItemQuantityPicker';
